import { Draft, produce } from 'immer';

import {
  GateTypes,
  MarketingOptInTypes,
  RegFieldsEnum,
  Registration,
  RegistrationQuestion,
  RegistrationStep,
  RegistrationStepType,
  RegistrationTypes,
} from '@/types/registration.types';
import { socialSSOTypes } from '@/utils/authentication/sso-utils';
import {
  GetDefaultRegistrationQuestions,
  getRegistrationQuestionsFromRegistrationSteps,
  sortRegistrationSteps,
  sortWorkingEventRegistrationQuestions,
} from '@/utils/registration/registration';
import buildWorkingRequirementsMap from '../components/gating/helpers/build-working-requirements-map';
import applyGatingDerivedRegistrationFields from '../components/gating/helpers/apply-gating-derived-registration-fields';
import deriveActiveGateTypes from '../components/gating/helpers/derive-active-gate-types';

/**
 * Single source of truth for "what fields change in the working project when the user
 * makes an access-control change". Three callers (AccessTypeSelector, GatingRegistrationSettings,
 * RenderSignInOption) all funnel through here so the events-3-parity coercions
 * (password ↔ SSO mutual exclusion, SSO ↔ recaptcha/marketing/in-person, social-only vs custom
 * SSO discrimination, gating-derived form fields, etc.) live in exactly one place.
 *
 * The transform is pure. Callers handle the dispatch (and any UI feedback such as toasts).
 */

export type AuthFlowMode = 'native' | 'sso' | 'thirdparty' | 'disabled';

export type RegistrationMutation =
  | { kind: 'auth-flow'; mode: AuthFlowMode }
  | {
      kind: 'gate';
      gateType: GateTypes | null;
      domainGateMode: 'allow' | 'block';
      allowDomains: string[];
      blockDomains: string[];
      sharedPasscode: string;
    }
  | { kind: 'social-signin'; provider: string; on: boolean };

export type RegistrationCurrentState = {
  registration_settings: Registration;
  // Required for 'auth-flow' and 'gate' mutations. Safe to omit for 'social-signin' since
  // that branch only touches registration_settings.
  registration_steps?: RegistrationStep[];
  registration_questions?: RegistrationQuestion[];
  required_registration_questions?: number[] | null | undefined;
};

export type RegistrationUpdate = {
  registration_settings: Registration;
  registration_steps?: RegistrationStep[];
  registration_questions?: RegistrationQuestion[];
  required_registration_questions?: number[] | null;
};

export function deriveRegistrationUpdate(
  current: RegistrationCurrentState,
  mutation: RegistrationMutation,
  channelRegistrationQuestions: RegistrationQuestion[]
): RegistrationUpdate {
  switch (mutation.kind) {
    case 'auth-flow':
      return applyAuthFlowMutation(current, mutation);
    case 'gate':
      return applyGateMutation(current, mutation, channelRegistrationQuestions);
    case 'social-signin':
      return applySocialSignInMutation(current, mutation);
  }
}

// ---------- AUTH FLOW ----------

function applyAuthFlowMutation(
  current: RegistrationCurrentState,
  mutation: { mode: AuthFlowMode }
): RegistrationUpdate {
  const settings = current.registration_settings;
  const { mode } = mutation;
  const isSso = mode === 'sso';
  const isThirdParty = mode === 'thirdparty';
  const isNative = mode === 'native';
  const isDisabled = mode === 'disabled';

  // 3rd Party and Disabled force open registration; Native restores type from the effective
  // gate (gated if any gate is configured, open otherwise). Use the legacy-aware derivation so
  // events-3 data (project_gate_type === undefined, gate configured via legacy signals) isn't
  // treated as ungated and flipped to open, which would drop the gate.
  //
  // SSO also forces open EXCEPT when the audience list (passcode_list) gate is the SOLE
  // active gate: it is the one gate allowed to coexist with SSO (BDEV-5154) — the registration
  // service ANDs the SSO auth and the audience-list membership check. Restore semantics (not
  // just preserve): entering SSO from 3rd Party/Disabled, where type was forced open but
  // project_gate_type still points at passcode_list, re-gates the project.
  //
  // The sole-gate requirement is load-bearing for legacy multi-gate data (project_gate_type
  // === undefined can derive several coexisting gates): keeping type=gated there would
  // publish EVERY legacy requirement un-narrowed (events-api apply-effective-requirements
  // no-ops while the pointer is undefined), so a coexisting shared_passcode gate would reject
  // every SSO registrant (the flattened SSO form has no passcode field) and password/domain
  // gates would co-fire invisibly. Any coexisting gate must force open, as before BDEV-5154.
  const activeGates = deriveActiveGateTypes(settings);
  const ssoKeepsAudienceGate = isSso && activeGates.length === 1 && activeGates[0] === GateTypes.passcode_list;
  const shouldOpenGate = (isSso && !ssoKeepsAudienceGate) || isThirdParty || isDisabled;
  const restoredNativeType = isNative
    ? activeGates.length > 0
      ? RegistrationTypes.gated
      : RegistrationTypes.open
    : undefined;

  const nextSettings = produce(settings, (draft) => {
    ensureSingleSignOn(draft);
    draft.externalRegistration = { ...(draft.externalRegistration ?? {}), isOn: isThirdParty };
    draft.singleSignOn.isOn = isSso;
    // When entering SSO mode, drop social sign-on entries that were saved while in Native mode —
    // those belong to the Native social-toggle UI, not the SSO panel.
    if (isSso) {
      draft.singleSignOn.singleSignOns = (draft.singleSignOn.singleSignOns ?? []).filter((sso) => {
        return !socialSSOTypes.includes(sso);
      });
    }

    if (shouldOpenGate) {
      draft.type = RegistrationTypes.open;
    } else if (ssoKeepsAudienceGate) {
      draft.type = RegistrationTypes.gated;
    } else if (restoredNativeType !== undefined) {
      draft.type = restoredNativeType;
    }

    // Coercions when entering CUSTOM SSO mode (AccessTypeSelector → 'sso'). These rules apply
    // only when the user is replacing native auth with a custom SSO provider (SAML/OIDC/oAuth).
    // Social sign-in (Google) goes through `applySocialSignInMutation` and intentionally does
    // NOT touch any of these — picking Google as a Native add-on shouldn't disable recaptcha,
    // wipe marketing opt-in, or turn off in-person attendee mode.
    //
    // events-3 parity references: SET_REGISTRATION_SINGLE_SIGN_ON reducer + single-sign-on.tsx.
    if (isSso) {
      draft.enableRecaptcha = false;
      draft.marketing_email_options = MarketingOptInTypes.noOptIn;
      draft.adminInPersonAttendeeModeEnabled = false;
    }
  });

  // SSO and 3rd Party flatten the general step to default questions and make first/last
  // optional. Native and Disabled leave the steps as-is.
  if (isSso || isThirdParty) {
    const flattenedSteps = (current.registration_steps ?? []).map((step) =>
      step.type === RegistrationStepType.general ? { ...step, questions: GetDefaultRegistrationQuestions() } : step
    );
    const stepsUpdate = deriveStepsUpdate({
      steps: flattenedSteps,
      makeNameOptional: true,
      currentRequiredRegistrationQuestions: current.required_registration_questions,
    });
    return {
      registration_settings: nextSettings,
      registration_steps: stepsUpdate.registration_steps,
      registration_questions: stepsUpdate.registration_questions,
      required_registration_questions: stepsUpdate.required_registration_questions,
    };
  }

  return { registration_settings: nextSettings };
}

// ---------- GATE ----------

function applyGateMutation(
  current: RegistrationCurrentState,
  mutation: {
    gateType: GateTypes | null;
    domainGateMode: 'allow' | 'block';
    allowDomains: string[];
    blockDomains: string[];
    sharedPasscode: string;
  },
  channelRegistrationQuestions: RegistrationQuestion[]
): RegistrationUpdate {
  const settings = current.registration_settings;
  const { gateType, domainGateMode, allowDomains, blockDomains, sharedPasscode } = mutation;
  const isPassword = gateType === GateTypes.password;

  const requirements = buildWorkingRequirementsMap({
    sourceMap: settings.requirements ?? {},
    access: gateType,
    allowDomains,
    sharedPasscode,
  });

  const nextSettings = produce(settings, (draft) => {
    ensureSingleSignOn(draft);
    draft.type = gateType === null ? RegistrationTypes.open : RegistrationTypes.gated;
    draft.project_gate_type = gateType ?? null;
    draft.requirements = requirements;
    draft.blockedDomainEmails = blockDomains;
    draft.useBlockedDomainEmails = blockDomains.length > 0;
    draft.password_gating_enabled = isPassword;
    draft.domain_gate_mode = domainGateMode;

    // events-3 parity: password gating is mutually exclusive with SSO and forces a few
    // adjacent settings. See events-3 TOGGLE_PASSWORD_GATING reducer.
    if (isPassword) {
      draft.singleSignOn.isOn = false;
      draft.singleSignOn.singleSignOns = [];
      draft.sendEmailConfirmation = true;
      draft.skipEmailOnDuplicateRegistration = false;
    }
  });

  // Sync the registration form's gating-driven fields (passcode/email) with the new gate.
  const fieldsUpdate = applyGatingDerivedRegistrationFields({
    newGateType: gateType,
    registrationSteps: current.registration_steps ?? [],
    registrationQuestions: current.registration_questions ?? [],
    requiredRegistrationQuestions: current.required_registration_questions,
    channelRegistrationQuestions,
  });

  return {
    registration_settings: nextSettings,
    ...(fieldsUpdate
      ? {
          registration_steps: fieldsUpdate.registrationSteps,
          registration_questions: fieldsUpdate.registrationQuestions,
          required_registration_questions: fieldsUpdate.requiredRegistrationQuestions,
        }
      : {}),
  };
}

// ---------- SOCIAL SIGN-IN ----------

function applySocialSignInMutation(
  current: RegistrationCurrentState,
  mutation: { provider: string; on: boolean }
): RegistrationUpdate {
  const settings = current.registration_settings;
  const { provider, on } = mutation;
  // Only one social sign-in active at a time; toggling on replaces the array.
  const singleSignOns = on ? [provider] : [];

  // Live event's registration form gates social SSO display on `singleSignOn.isOn` AND
  // `singleSignOns.length`. Keep `isOn` in lockstep with provider presence so Google
  // actually renders on the published page. AccessTypeSelector distinguishes Native + social
  // from full SSO mode by inspecting whether singleSignOns are social-only.
  //
  // events-3 parity: turning any SSO on (social included) forces `enableRecaptcha = false`
  // in the published bundle. See SET_REGISTRATION_SINGLE_SIGN_ON reducer (events-3) — same
  // "force off on enter, preserve previous value on exit" pattern as `applyAuthFlowMutation`.
  // `marketing_email_options` and `adminInPersonAttendeeModeEnabled` are intentionally left
  // alone here; those coercions are reserved for transitions into custom SSO mode, where the
  // native form is replaced entirely.
  const nextSettings = produce(settings, (draft) => {
    ensureSingleSignOn(draft);
    draft.singleSignOn.isOn = singleSignOns.length > 0;
    draft.singleSignOn.singleSignOns = singleSignOns;
    if (singleSignOns.length > 0) {
      draft.enableRecaptcha = false;
    }
  });

  return { registration_settings: nextSettings };
}

// ---------- shared helpers ----------

// Legacy projects (created before SingleSignOnSettings was added to events-api on 2024-02-05,
// plus all non-Townhall projects created before backend now seeds the field for every type)
// can come back from the API with `registration_settings.singleSignOn === undefined`. The
// TS type declares it required, so produce() callbacks would NPE on the first mutation.
// Normalize at the top of every callback that touches it.
function ensureSingleSignOn(draft: Draft<Registration>): void {
  if (!draft.singleSignOn) {
    draft.singleSignOn = { isOn: false, displayTop: true };
  }
}

/**
 * Mirrors the derivation logic in update-registration-steps-reducer.ts so callers can
 * include the resulting fields in a single saveWorkingProjectByFields dispatch.
 */
function deriveStepsUpdate({
  steps,
  makeNameOptional,
  requiredQuestions,
  currentRequiredRegistrationQuestions,
}: {
  steps: RegistrationStep[];
  makeNameOptional?: boolean;
  requiredQuestions?: number[];
  currentRequiredRegistrationQuestions: number[] | null | undefined;
}): {
  registration_steps: RegistrationStep[];
  registration_questions: RegistrationQuestion[];
  required_registration_questions: number[] | null;
} {
  const sortedSteps = sortRegistrationSteps(steps);
  const derivedQuestions = getRegistrationQuestionsFromRegistrationSteps(sortedSteps);
  const ordered = sortWorkingEventRegistrationQuestions({
    registrationSteps: sortedSteps,
    registrationQuestions: derivedQuestions,
  });
  const deduped = Array.from(new Map(ordered.map((q) => [q.registration_question, q])).values());

  const required = makeNameOptional
    ? [RegFieldsEnum.email]
    : (requiredQuestions ?? currentRequiredRegistrationQuestions ?? null);

  return {
    registration_steps: sortedSteps || [],
    registration_questions: deduped,
    required_registration_questions: required,
  };
}
